import type {MouseEventHandler, ReactElement} from 'react';
import React, {useEffect, useMemo, useRef, useState} from 'react';
import type {AiVoicePanelProps, GeneratedAudioData, PromptFilter, SelectedPromptFilter} from '@Panels/ai-voice-panel/ai-voice-panel.types';
import {
  AiVoicePanelDataKey,
  AiVoicePanelErrorState,
  AiVoicePanelGenerationState,
  AiVoicePanelHeightChangingEvents,
  AiVoicePanelState,
  DEFAULT_VOICE_SUBFILTER,
  PROMPT_CHARACTER_LIMIT,
  PROMPT_FILTERS,
  PromptFilterId,
} from '@Panels/ai-voice-panel/ai-voice-panel.types';
import {Panel, PanelContent, PanelFooter, PanelHeader} from '@Components/panel';
import {Button, Size, Type} from '@Components/button';
import {isMobile} from 'react-device-detect';
import type {PanelHeaderActionProps} from '@Components/panel/components/panel-header-action';
import {IconSize} from '@Components/icon-v2/icon.types';
import {AiButton} from '@Components/ai-button';
import {InputField} from '@Components/input-field-v2';
import {InputFieldState, InputFieldType} from '@Components/input-field-v2/input-field.types';
import {AIButtonVariant} from '@Components/ai-button/ai-button.types';
import {PillButton} from '@Components/pill-button';
import {PillButtonSize} from '@Components/pill-button/pill-button.types';
import {getIconForPromptFilter, getLoadingKeyForAiVoice} from '@Panels/ai-voice-panel/ai-voice-panel.library';
import {Dropdown} from '@Components/dropdown-v2';
import {ALIGNMENT_TYPE, DROPDOWN_POSITION} from '@Components/dropdown-v2/dropdown.types';
import {
  GENERATE_SPEECH_DAILY_LIMIT_REACHED,
  generateSpeech,
  getGridItemForGeneratedSpeech,
  getRecentlyGeneratedTranscripts,
  getTitleForGeneratedSpeech,
  getUrlForGeneratedAiSpeech,
  getUserGeneratedSpeech,
  reWriteTranscript,
  showTextToSpeechAiWriterUpsellDialog,
  showTextToSpeechUpsellDialog,
  writeTranscript,
} from '@Libraries/text-to-speech.library';
import {AudioItem} from '@Components/base-grid/components/audio-item/audio-item';
import {SkeletonBrick} from '@Components/skeleton-brick';
import RecentGeneratedVoiceView from '@Panels/ai-voice-panel/components/recent-generated-voice-view';
import styles from './ai-voice-panel.module.scss';
import {useAppDispatch, useAppSelector} from '@/hooks';
import type {AiGeneratedContent, ErrorType, PromptBoxTitles, SelectedValueType} from '@Components/ai-text-prompt-box/ai-text-prompt-box.types';
import {AiPromptBoxVariant, PromptBoxConfigOptionType} from '@Components/ai-text-prompt-box/ai-text-prompt-box.types';
import {AiTextPromptBox} from '@Components/ai-text-prompt-box';
import {noop} from '@Utils/general.util';
import type {ContainerQueryObject} from '@Hooks/useContainerQuery';
import {useContainerQuery} from '@Hooks/useContainerQuery';
import type {GridSkeletonItemStorage} from '@Components/base-grid/components/grid-skeleton-item';
import {GRID_ITEM_TYPE} from '@Components/base-grid/components/grid-item';
import {addItemsToGrid, deleteGridItems, getNoOfGridItems, replaceGridItem} from '@Components/base-grid';
import {Text, TextSize, TextType} from '@Components/text';
import {getAssetUrl} from '@Utils/s3.util';
import {getUserId, isUserPremium, USER_LOGGED_OUT_ERROR_CODE} from '@Libraries/user.library';
import {addClickOutsideListener} from '@Utils/dom.util';
import {ClickableDiv} from '@Components/clickable-div';
import {getFilenameFromURL} from '@Utils/url.util';
import {updatePanelData} from '@Components/panel/panel-reducer';
import {Icon, type IconProps} from '@Components/icon-v2';
import {openErrorModal} from '@Modals/error-modal';
import type {PanelMessageProp} from '@Components/panel/panel.types';
import {PANEL_MESSAGE_TYPE} from '@Components/panel/panel.types';
import type {GridStockAudioItemStorage} from '@Components/base-grid/components/grid-stock-audio-item';
import {GA4EventName, trackPosterBuilderGA4Events} from '@Libraries/ga-events';
import {hideLoading, showLoading} from '@Libraries/loading-toast-library';
import {AiVoiceState} from '@Components/poster-editor/components/poster-ai/poster-ai.types';
import {updateAiVoiceState} from '@Components/poster-editor/poster-editor-reducer';
import useCustomPanelClose from '@Panels/hooks/useCustomPanelClose';
import {useTopBottomScrollShadow} from '@Hooks/useTopBottomScrollShadow';
import {AiVoicePromptFilterPopupWrapper} from '@Panels/ai-voice-panel/components/ai-voice-prompt-filter-popup-wrapper';

const query: ContainerQueryObject = {
  small: {
    minWidth: 0,
    maxWidth: 484,
  },
  large: {
    minWidth: 485,
  },
};

const DEFAULT_GRID_ITEM_WIDTH = 250;
const DEFAULT_GRID_ITEM_HEIGHT = 78;
const AI_WRITER_PSEUDO_DROPDOWN_CONTAINER_ID_PREFIX = 'tts-ai-writer';

const LOADING_ITEM_FOR_GRID: GridSkeletonItemStorage = {
  id: 'ai-void-grid-loading-item-id',
  type: GRID_ITEM_TYPE.SKELETON,
  width: DEFAULT_GRID_ITEM_WIDTH,
  height: DEFAULT_GRID_ITEM_HEIGHT,
};

const AI_HEADER_ICON_PROPS: IconProps = {
  icon: 'icon-ai-voice',
  size: IconSize.SIZE_ICON_20,
  className: `${styles.aiIcon} spacing-m-r-1`,
};

export default function AiVoicePanel({isOpenedInPopOver = false, ...props}: AiVoicePanelProps): ReactElement {
  const panelRef = useRef<HTMLDivElement>(null);
  const isPremiumRef = useRef(typeof getUserId() !== 'undefined' && isUserPremium());
  const param = useContainerQuery(panelRef, query);
  const availableFilters = props.allowedFilters ?? PROMPT_FILTERS;
  const [panelState, setPanelState] = useState(AiVoicePanelState.DEFAULT);
  const [selectedFilters, setSelectedFilters] = useState<SelectedPromptFilter[]>([]);
  const [errorState, setErrorState] = useState<AiVoicePanelErrorState | undefined>(undefined);
  const [prompt, setPrompt] = useState('');
  const [generatedAudio, setGeneratedAudio] = useState<GeneratedAudioData | undefined>(undefined);
  const [isAiWriterDropdownOpen, setIsAiWriterDropdownOpen] = useState<boolean>(false);
  const [areRecentVoiceAvailable, setAreRecentVoiceAvailable] = useState<boolean>(false);
  const [noGeneratedAudioInfographicLoaded, setNoGeneratedAudioInfographicLoaded] = useState<boolean>(false);
  const [openedFilterItem, setOpenedFilterItem] = useState<PromptFilterId | undefined>(undefined);
  const [inputErrorState, setInputErrorState] = useState<boolean>(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const lastPrompt = useRef<string | undefined>(undefined);
  const writingIdToReuse = useRef<string | undefined>(undefined);
  const dispatch = useAppDispatch();
  const panelId = useRef(props.panelId ?? 'AI_VOICE_PANEL_NO_ID');
  const gridRef = useRef<HTMLDivElement | null>(null);
  const {topShadow} = useTopBottomScrollShadow(gridRef, !!gridRef.current);
  const aiWriterDropdownId = useMemo(() => {
    return `${AI_WRITER_PSEUDO_DROPDOWN_CONTAINER_ID_PREFIX}-${props.panelId ?? 'AI_VOICE_PANEL_NO_ID'}`;
  }, [panelId.current]);
  const close = useCustomPanelClose(props.panelId ?? 'AI_VOICE_PANEL_NO_ID', props.onClose?.bind(null) ?? noop, props.hideOnClose);
  const recentGeneratedVoiceGridId = useMemo(() => {
    return `${props.panelId}-recent-voice-generations`;
  }, [props.panelId]);
  const getPanelMessage = useMemo((): PanelMessageProp | undefined => {
    if (errorState === AiVoicePanelErrorState.DAILY_LIMIT_REACHED && param.large) {
      return {
        message: window.i18next.t('pmwjs_text_to_speech_daily_limit_reached_error_message'),
        type: PANEL_MESSAGE_TYPE.ERROR,
      };
    }
    return undefined;
  }, [errorState]);
  const numberOfGridItems = useAppSelector((state) => {
    return getNoOfGridItems(state, recentGeneratedVoiceGridId);
  });
  const aiVoicePanelGenerationState = useAppSelector((state): AiVoicePanelGenerationState | undefined => {
    return state.panels.panelHashmap[panelId.current]?.data?.[AiVoicePanelDataKey.GENERATION_STATE] as AiVoicePanelGenerationState;
  });

  const onBack = (e?: unknown): void => {
    if (panelState === AiVoicePanelState.DEFAULT && !isAiWriterDropdownOpen && props.onBack) {
      props.onBack(e);
    } else if (panelState === AiVoicePanelState.RECENT_VOICE) {
      setPanelState(AiVoicePanelState.DEFAULT);
    } else if (panelState === AiVoicePanelState.AI_WRITER) {
      if (param.large) {
        setIsAiWriterDropdownOpen(false);
      } else {
        setPanelState(AiVoicePanelState.DEFAULT);
      }
    }
  };

  const onClose = (): void => {
    if (aiVoicePanelGenerationState === AiVoicePanelGenerationState.GENERATING) {
      showLoading(getLoadingKeyForAiVoice(panelId.current), {text: window.i18next.t('pmwjs_generating_audio')});
    }
    close();
  };

  const getFooter = (): ReactElement | null => {
    if (panelState === AiVoicePanelState.AI_WRITER || param.large) {
      return null;
    }
    return <PanelFooter key="ai-voice-panel-footer" primaryActions={getPrimaryAction()} border={param.large} fullWidthPrimaryActions />;
  };

  const updateGenerationState = (state: AiVoicePanelGenerationState): void => {
    dispatch(
      updatePanelData({
        panelId: panelId.current,
        data: {
          [AiVoicePanelDataKey.GENERATION_STATE]: state,
        },
      })
    );
    updateGlobalGenerationState(state);
  };

  const updateGlobalGenerationState = (state: AiVoicePanelGenerationState): void => {
    let globalAiVoiceState: AiVoiceState;
    switch (state) {
      case AiVoicePanelGenerationState.IDLE:
        globalAiVoiceState = AiVoiceState.IDLE;
        break;
      case AiVoicePanelGenerationState.ERROR:
        globalAiVoiceState = AiVoiceState.ERROR;
        break;
      case AiVoicePanelGenerationState.GENERATING:
        globalAiVoiceState = AiVoiceState.GENERATING;
        break;
      case AiVoicePanelGenerationState.READY:
        globalAiVoiceState = AiVoiceState.READY;
        break;
      default:
        throw new Error(`Unhandled state for global ai voice state: ${state}`);
    }
    dispatch(updateAiVoiceState(globalAiVoiceState));
  };

  const onGenerateVoice = async (): Promise<void> => {
    setErrorState(undefined);
    updateGenerationState(AiVoicePanelGenerationState.GENERATING);
    updatePanelState(AiVoicePanelHeightChangingEvents.GENERATING);
    if (param.large) {
      dispatch(
        addItemsToGrid({
          id: recentGeneratedVoiceGridId,
          items: [LOADING_ITEM_FOR_GRID],
          index: 0,
        })
      );
    }

    setPrompt((prevState) => {
      setGeneratedAudio(undefined);
      lastPrompt.current = prevState.trim();
      return prevState.trim();
    });

    const voiceId =
      selectedFilters.find((selection) => {
        return selection.filterId === PromptFilterId.VOICE;
      })?.subfilterId ?? DEFAULT_VOICE_SUBFILTER;

    try {
      const response = await generateSpeech({text: prompt.trim(), voiceId});
      setGeneratedAudio(() => {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        const {id, duration, name, used_on, filename} = response;
        updatePanelState(AiVoicePanelHeightChangingEvents.GENERATED_RESULT);
        if (param.large) {
          dispatch(replaceGridItem({gridId: recentGeneratedVoiceGridId, itemId: LOADING_ITEM_FOR_GRID.id, gridItem: getGridItemForGeneratedSpeech(response)}));
        }
        return {
          id,
          duration,
          title: getTitleForGeneratedSpeech(name, used_on),
          source: getUrlForGeneratedAiSpeech(filename),
        };
      });
      updateGenerationState(AiVoicePanelGenerationState.READY);
      setTimeout(() => {
        updateGenerationState(AiVoicePanelGenerationState.IDLE);
      }, 300);
      hideLoading(getLoadingKeyForAiVoice(panelId.current));
    } catch (e) {
      onGenerateVoiceError(e as Error);
    }
  };

  const onGenerateVoiceError = (e: Error): void => {
    if (param.large) {
      dispatch(deleteGridItems({gridId: recentGeneratedVoiceGridId, gridSelectionGroupId: recentGeneratedVoiceGridId, itemIdsToDelete: [LOADING_ITEM_FOR_GRID.id]}));
    }

    updateGenerationState(AiVoicePanelGenerationState.ERROR);

    if (e.message === GENERATE_SPEECH_DAILY_LIMIT_REACHED) {
      trackPosterBuilderGA4Events(GA4EventName.AI_VOICE_LIMIT_REACHED);
      setErrorState(AiVoicePanelErrorState.DAILY_LIMIT_REACHED);
      return;
    }

    updatePanelState(AiVoicePanelHeightChangingEvents.DEFAULT);
    openErrorModal({message: window.i18next.t('pmwjs_internet_error_message')});
  };

  const getPrimaryAction = (): ReactElement[] | undefined => {
    switch (panelState) {
      case AiVoicePanelState.DEFAULT:
        return [
          <AiButton
            isFullWidth
            isLoadingButtonOverlayShown
            size={Size.SMALL}
            customClasses="_full-width _full-height"
            key="icon-ai-voice"
            text={lastPrompt.current === prompt ? window.i18next.t('pmwjs_generate_again') : window.i18next.t('pmwjs_generate')}
            icon="icon-ai-voice"
            onClick={onCTAClick}
            isLoading={isAiVoiceGenerating()}
            disabled={isGenerateCTADisabled()}
            variant={!isPremiumRef.current ? AIButtonVariant.PREMIUM : undefined}
          />,
        ];
      case AiVoicePanelState.AI_WRITER:
      case AiVoicePanelState.RECENT_VOICE:
        return undefined;
      default:
        throw new Error(`Unhandled panel state for footer primary action: ${panelState}`);
    }
  };

  const onCTAClick = async (): Promise<void> => {
    switch (panelState) {
      case AiVoicePanelState.DEFAULT:
        return isPremiumRef.current ? onGenerateVoice() : showTextToSpeechUpsellDialog();
      default:
        throw new Error(`Unhandled callback handler for panel state: ${panelState}`);
    }
  };

  const shouldShowHeaderBorder = (): boolean => {
    if (param.large) {
      return true;
    }
    return param.small && topShadow && !isPanelDefault();
  };

  const getHeader = (): ReactElement | null => {
    if (panelState === AiVoicePanelState.AI_WRITER) {
      return null;
    }

    return (
      <PanelHeader
        backButtonIconSize={isOpenedInPopOver ? IconSize.SIZE_ICON_16 : undefined}
        titleClasses={getTitleClasses()}
        className={`${shouldShowHeaderBorder() ? '' : '_borderless'} ${isOpenedInPopOver ? 'spacing-p-l-4 spacing-p-r-4' : ''}`}
        title={getTitle()}
        showCloseButton={param.large}
        onClose={param.large ? onClose : undefined}
        showBackButton
        onBackButton={onBack}
        actions={getActionForHeader()}
        forceCenterAlignTitleWithActionItems
        isOpenedInModal={isMobile}
      />
    );
  };

  const onRecentActionClicked: MouseEventHandler = (event): void => {
    event.stopPropagation();
    event.preventDefault();
    if (isAiVoiceGenerating()) {
      return;
    }
    if (panelState === AiVoicePanelState.DEFAULT) {
      setPanelState(AiVoicePanelState.RECENT_VOICE);
    } else {
      throw new Error(`Bad case to trigger recents`);
    }
  };

  const getActionForHeader = (): PanelHeaderActionProps[] | undefined => {
    if (param.large) {
      return undefined;
    }
    switch (panelState) {
      case AiVoicePanelState.DEFAULT:
        return areRecentVoiceAvailable
          ? [
              {
                key: 'ai-voice-header-recents',
                text: !isMobile ? window.i18next.t('pmwjs_recents') : undefined,
                icon: `icon-history ${isAiVoiceGenerating() ? 'content-disabled' : ''}`,
                className: `${isMobile ? styles.smallHeaderAction : ''} ${isAiVoiceGenerating() ? '_cursor-not-allowed' : ''}`,
                iconSize: isMobile ? IconSize.SIZE_ICON_20 : IconSize.SIZE_ICON_16,
                onClick: onRecentActionClicked,
                title: window.i18next.t('pmwjs_recents'),
                titleClassName: isAiVoiceGenerating() ? styles.disabledHeaderActionText : '',
              },
            ]
          : undefined;
      case AiVoicePanelState.AI_WRITER:
      case AiVoicePanelState.RECENT_VOICE:
        return undefined;
      default:
        throw new Error(`Unhandled panel state for header action: ${panelState}`);
    }
  };

  const getTitle = (): string => {
    switch (panelState) {
      case AiVoicePanelState.AI_WRITER:
        return window.i18next.t('pmwjs_ai_writer');
      case AiVoicePanelState.DEFAULT:
        return isAiVoiceGenerating() ? `${window.i18next.t('pmwjs_generating')}...` : window.i18next.t('pmwjs_ai_voice');
      case AiVoicePanelState.RECENT_VOICE:
        return window.i18next.t('pmwjs_recent_generations');
      default:
        throw new Error(`Unhanlded panel state for title: ${panelState}`);
    }
  };

  const getTitleClasses = (): string => {
    switch (panelState) {
      case AiVoicePanelState.AI_WRITER:
      case AiVoicePanelState.DEFAULT:
        return styles.aiText;
      case AiVoicePanelState.RECENT_VOICE:
        return '';
      default:
        throw new Error(`Unhanlded panel state for title: ${panelState}`);
    }
  };

  const getEmptyGridInfographic = (): ReactElement => {
    return (
      <div className={`_full-width flex-v-row flex-center ${styles.noGeneratedAudiosContainer}`}>
        {
          <img
            onLoad={() => {
              setNoGeneratedAudioInfographicLoaded(true);
            }}
            src={getAssetUrl('text-to-speech-infographic.png')}
            alt={window.i18next.t('pmwjs_text_to_speech_infographic_text')}
            className={noGeneratedAudioInfographicLoaded ? '' : '_hidden'}
          />
        }
        {!noGeneratedAudioInfographicLoaded ? <SkeletonBrick className={styles.imagePlaceholder} isPulsating /> : null}
        <Text type={TextType.BODY} size={TextSize.XSMALL} val={window.i18next.t('pmwjs_text_to_speech_infographic_text')} />
      </div>
    );
  };

  const getRecentVoiceView = (): ReactElement => {
    return (
      <>
        {!areRecentVoiceAvailable || numberOfGridItems <= 0 ? getEmptyGridInfographic() : null}
        <RecentGeneratedVoiceView
          className={`spacing-p-b-3 ${!areRecentVoiceAvailable || numberOfGridItems <= 0 ? '_hidden' : ''}`}
          gridId={recentGeneratedVoiceGridId}
          gridSelectionGroupId={recentGeneratedVoiceGridId}
          maxSelectionNumber={1}
          customOnGridItemClicked={(item) => {
            void props.onAddAudioItem(item as GridStockAudioItemStorage);
          }}
          customDimensions={{width: DEFAULT_GRID_ITEM_WIDTH, height: DEFAULT_GRID_ITEM_HEIGHT}}
          gridRef={(ref) => {
            gridRef.current = ref;
          }}
        />
      </>
    );
  };

  const aiWriterContainerRef = (el: HTMLDivElement): void => {
    if (!el) {
      return;
    }

    addClickOutsideListener(`#${el.getAttribute('id')}`, () => {
      setIsAiWriterDropdownOpen(false);
    });
  };

  const getAiWriterView = (): ReactElement => {
    const onContentSelected = (selectedContent: AiGeneratedContent): void => {
      setPrompt(selectedContent.text);
      if (isAiWriterDropdownOpen) {
        setIsAiWriterDropdownOpen(false);
      }
    };

    const useContent = (): Promise<void> => {
      return new Promise(noop);
    };

    const reuseContent = (id: string): Promise<void> => {
      return new Promise((res) => {
        writingIdToReuse.current = id;
        res();
      });
    };

    const alterContent = (_prompt: string, selectedValues: SelectedValueType): Promise<AiGeneratedContent> => {
      if (writingIdToReuse.current) {
        return reWriteTranscript(writingIdToReuse.current, selectedValues);
      }

      throw new Error('Race condition hit. Generated writing id is undefined. reuseContent should have been called before alterContent.');
    };

    const titles: PromptBoxTitles = {
      main: window.i18next.t('pmwjs_ai_writer'),
      recents: window.i18next.t('pmwjs_recent_generations'),
      selected: window.i18next.t('pmwjs_selected_script'),
      primarySelectedContent: '',
    };

    const alterationLoadingText = {
      primaryAlterationLoadingMessage: window.i18next.t('pmwjs_updating'),
    };

    return (
      <div className={'_full-height'}>
        <AiTextPromptBox
          ref={undefined}
          isOpenedInPopOver={isOpenedInPopOver}
          showBackButton={param.small ? param.small : !isAiWriterDropdownOpen}
          sectionsContainerClass={`_full-height ${styles.aiWriterNoGradientBorder}`}
          promptboxContainerClass={'_full-height'}
          contentClassName={`spacing-p-r-0 spacing-p-l-0 flex-v-row`}
          innerContentClassName={`spacing-p-r-3 spacing-p-l-3 _full-height ${param.large ? styles.aiWriterPopupInnerContent : ''}`}
          isInMobileMode={isMobile}
          titles={titles}
          onContentSelected={onContentSelected}
          getRecents={getRecentlyGeneratedTranscripts.bind(null, 20, 0)}
          generateContent={writeTranscript}
          onBack={onBack}
          mainPlaceholder={window.i18next.t('pmwjs_text_to_speech_ai_writer_placeholder')}
          variant={AiPromptBoxVariant.MONOCONTENT}
          optionsToUse={[PromptBoxConfigOptionType.LENGTH, PromptBoxConfigOptionType.TONE]}
          onClose={isMobile ? onClose : undefined}
          onPromptChange={noop}
          alterationLoadingTexts={alterationLoadingText}
          useContent={useContent}
          reuseContent={reuseContent}
          alterContent={alterContent}
        />
      </div>
    );
  };

  const getLoadingContainer = (): ReactElement | null => {
    return isAiVoiceGenerating() ? <SkeletonBrick isPulsating height={77} className="radius-4" /> : null;
  };

  const getErrorContainer = (): ReactElement | null => {
    if (errorState === AiVoicePanelErrorState.DAILY_LIMIT_REACHED) {
      return (
        <div className={`spacing-p-t-2 spacing-p-r-3 spacing-p-b-2 spacing-p-l-3 flex-row-align-center radius-8 ${styles.dailyLimitErrorContainer}`}>
          <Icon className={'content-danger'} icon={'icon-exclamation-triangle'} />
          <Text
            disableSelection
            className={'content-danger spacing-m-l-1'}
            type={TextType.BODY}
            size={TextSize.XSMALL}
            val={window.i18next.t('pmwjs_text_to_speech_daily_limit_reached_error_message')}
          />
        </div>
      );
    }
    return null;
  };

  const getAiVoiceView = (): ReactElement => {
    return (
      <div className={`flex-v-row ${param.large ? '_full-height ' : ''} ${styles.aiVoiceViewContainer}`}>
        <div className="_position-relative">
          {getPromptInputContainer()}
          {param.large ? null : <div className={`${styles.filterContainerSmall} spacing-m-t-3`}>{getFiltersForPrompt()}</div>}
        </div>
        <div className={param.large ? styles.scrollableContent : 'spacing-p-t-9'}>
          {param.large ? null : getLoadingContainer()}
          {param.large ? null : getResultsContainer()}
          {param.large ? null : getErrorContainer()}
          {param.large ? (
            <div className={`_full-width ${styles.generatedAudioGridContainer} ${param.small ? styles.smallGridContainer : ''} ${isAiWriterDropdownOpen ? styles.activeBlur : ''}`}>
              {getRecentVoiceView()}
            </div>
          ) : null}
        </div>
      </div>
    );
  };

  const onFilterDeselected = (filterId: PromptFilterId): void => {
    setSelectedFilters((staleState) => {
      return staleState.filter((selection) => {
        return selection.filterId !== filterId;
      });
    });
  };

  const getPopUpForFilter = (filterItem: PromptFilter): ReactElement => {
    const selectedSubfilter = selectedFilters.find((selection) => {
      return selection.filterId === filterItem.id;
    })?.subfilterId;

    return (
      <AiVoicePromptFilterPopupWrapper
        id={filterItem.id}
        key={filterItem.id}
        subfilters={filterItem.subFilters}
        initialSelection={selectedSubfilter}
        onSelected={(id, event) => {
          event.stopPropagation();
          event.preventDefault();
          onFilterDropdownClose();
          setSelectedFilters((staleState) => {
            const otherFilters = staleState.filter((selection) => {
              return selection.filterId !== filterItem.id;
            });
            otherFilters.push({filterId: filterItem.id, subfilterId: id});
            return otherFilters;
          });
        }}
      />
    );
  };

  const getFiltersForPrompt = (): ReactElement => {
    return (
      <ul className={`spacing-p-0 spacing-m-0 flexbox flex-wrap ${styles.filterContainer} ${isAiWriterDropdownOpen ? styles.activeBlur : ''}`}>
        {availableFilters.map((filterItem): ReactElement => {
          const selected = selectedFilters.find((selection) => {
            return selection.filterId === filterItem.id;
          });
          const subFilter = selected
            ? filterItem.subFilters.find((subfilters) => {
                return subfilters.id === selected.subfilterId;
              })
            : undefined;
          const selector = (
            <PillButton
              fireOnClickWhenSelected={!isAiVoiceGenerating()}
              key={filterItem.id}
              isDisabled={isAiVoiceGenerating()}
              isSelected={!!selected}
              onClose={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onFilterDeselected(filterItem.id);
              }}
              size={param.large ? PillButtonSize.MEDIUM : PillButtonSize.SMALL}
              text={subFilter ? subFilter.name : filterItem.name}
              leftIconProps={{icon: getIconForPromptFilter(filterItem.id), size: IconSize.SIZE_ICON_16}}
              onClick={noop}
            />
          );
          return (
            <li key={`dropdown-container-${filterItem.id}`} className="spacing-p-0 spacing-m-0">
              <Dropdown
                isControlled
                onOpen={() => {
                  setOpenedFilterItem(filterItem.id);
                }}
                open={filterItem.id === openedFilterItem}
                onClose={onFilterDropdownClose}
                useDefaultArrow
                position={DROPDOWN_POSITION.FIXED}
                alignment={ALIGNMENT_TYPE.TOP_START}
                popUpHasCustomWidth
                selector={selector}
                popup={getPopUpForFilter(filterItem)}
                popupSpacingClass="spacing-m-0 spacing-p-3"
                popupClassName={`border-s-standard ${styles.popupClass}`}
                popUpHasCustomHeight
                showModalForSmallScreen
                modalTitle={filterItem.name}
              />
            </li>
          );
        })}
      </ul>
    );
  };

  const onFilterDropdownClose = (): void => {
    setOpenedFilterItem(undefined);
  };

  const getPromptInputContainer = (): ReactElement => {
    return (
      <div className={`flex-v-row ${styles.promptContainer}`}>
        <div className={styles.inputContainer}>
          <InputField
            autoFocus
            maxLength={PROMPT_CHARACTER_LIMIT}
            isControlled
            isMultiline
            inputType={InputFieldType.SEARCH}
            input={prompt}
            onInputChange={(updatedTerm: string): void => {
              setPrompt(() => {
                if (inputErrorState && updatedTerm.trim().length > 0) {
                  setInputErrorState(false);
                }
                return updatedTerm;
              });
            }}
            onInputSubmit={() => {
              if (isGenerateCTADisabled()) {
                setInputErrorState(true);
              } else {
                void onCTAClick();
              }
            }}
            isDisabled={isAiVoiceGenerating()}
            placeholder={window.i18next.t('pmwjs_text_to_speech_input_placeholder')}
            ref={inputRef}
            state={inputErrorState || errorState ? InputFieldState.ERROR : InputFieldState.DEFAULT}
          />
          {param.large ? null : (
            <div className={styles.aiWriterButton}>
              {isAiVoiceGenerating() ? (
                <Button type={Type.PRIMARY} text={window.i18next.t('pmwjs_write_with_ai')} size={Size.XSMALL} icon="icon-ai-caption" disabled />
              ) : (
                <AiButton
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleAiWriterTrigger();
                  }}
                  variant={isPremiumRef.current ? AIButtonVariant.SUBDUED : AIButtonVariant.PREMIUM_SUBDUED}
                  text={window.i18next.t('pmwjs_write_with_ai')}
                  size={Size.XSMALL}
                  icon="icon-ai-caption"
                  disabled={isAiVoiceGenerating()}
                  textClasses={styles.aiWriterButtonText}
                  className={isAiWriterDropdownOpen ? styles.aiWriterButtonActive : ''}
                />
              )}
            </div>
          )}
        </div>
        {param.large ? getFiltersAndButtonsRow() : null}
      </div>
    );
  };

  const handleAiWriterTrigger = (): void => {
    if (isPremiumRef.current) {
      if (param.large) {
        setIsAiWriterDropdownOpen((prevState) => {
          return !prevState;
        });
      } else {
        setPanelState(AiVoicePanelState.AI_WRITER);
      }
    } else {
      void showTextToSpeechAiWriterUpsellDialog();
    }
  };

  const getAiWriterDropdownSelector = (callback = noop): ReactElement => {
    return isAiVoiceGenerating() ? (
      <Button type={Type.PRIMARY} text={window.i18next.t('pmwjs_write_with_ai')} size={Size.SMALL} icon="icon-ai-caption" disabled />
    ) : (
      <AiButton
        onClick={callback}
        variant={isPremiumRef.current ? AIButtonVariant.SUBDUED : AIButtonVariant.PREMIUM_SUBDUED}
        text={window.i18next.t('pmwjs_write_with_ai')}
        size={Size.SMALL}
        icon="icon-ai-caption"
        disabled={isAiVoiceGenerating()}
        textClasses={styles.aiWriterButtonText}
        isActive={isAiWriterDropdownOpen}
      />
    );
  };

  const getFiltersAndButtonsRow = (): ReactElement => {
    return (
      <div className={`flex-h-row flex-justify-between ${styles.filtersAndButtonsContainer}`}>
        {getFiltersForPrompt()}
        <div className={`_full-height flex-h-row ${styles.buttonsContainer}`}>
          <div ref={aiWriterContainerRef} id={aiWriterDropdownId} className="_position-relative">
            {getAiWriterDropdownSelector(handleAiWriterTrigger)}
            {isAiWriterDropdownOpen ? (
              <>
                <div className={`shadow-floating-elevation-3 border-s-standard radius-4 _position-absolute ${styles.largeScreenAiWriterPopoverContainer}`}>{getAiWriterView()}</div>
                <div className={`border-s-standard radius-4 _position-absolute ${styles.dummyDropdownArrow}`} />
              </>
            ) : null}
          </div>
          <AiButton
            isLoadingButtonOverlayShown
            size={Size.SMALL}
            customClasses={`_full-width ${styles.generateButton} ${isAiWriterDropdownOpen ? styles.activeBlur : ''}`}
            key="icon-ai-voice"
            text={lastPrompt.current === prompt ? window.i18next.t('pmwjs_generate_again') : window.i18next.t('pmwjs_generate')}
            icon="icon-ai-voice"
            onClick={onCTAClick}
            isLoading={isAiVoiceGenerating()}
            disabled={isGenerateCTADisabled()}
            variant={!isPremiumRef.current ? AIButtonVariant.PREMIUM : undefined}
          />
        </div>
      </div>
    );
  };

  const isGenerateCTADisabled = (): boolean => {
    return !prompt.trim().length || !!errorState;
  };

  const getResultsContainer = (): ReactElement | null => {
    return generatedAudio ? (
      <ClickableDiv
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          if (generatedAudio) {
            void props.onAddAudioItem({
              type: GRID_ITEM_TYPE.STOCK_AUDIO,
              audioSrc: generatedAudio.source,
              duration: `${generatedAudio.duration}`,
              name: generatedAudio.title,
              id: `${generatedAudio.id}`,
              data: {
                hashedFilename: getFilenameFromURL(generatedAudio.source),
              },
            });
          }
        }}
      >
        <AudioItem
          className={`radius-12 ${styles.generatedAudioItem}`}
          showThreeDots={false}
          uid={`${generatedAudio.id}`}
          title={generatedAudio.title}
          duration={generatedAudio.duration}
          source={generatedAudio.source}
        />
      </ClickableDiv>
    ) : null;
  };

  const isAiVoiceGenerating = (): boolean => {
    return aiVoicePanelGenerationState === AiVoicePanelGenerationState.GENERATING;
  };

  const isPanelDefault = (): boolean => {
    return panelState === AiVoicePanelState.DEFAULT;
  };

  const getInnerContent = (): ReactElement => {
    switch (panelState) {
      case AiVoicePanelState.RECENT_VOICE:
        return getRecentVoiceView();
      case AiVoicePanelState.AI_WRITER:
        return getAiWriterView();
      case AiVoicePanelState.DEFAULT:
        return getAiVoiceView();
      default:
        throw new Error(`Unhanlded panel state for inner content: ${panelState}`);
    }
  };

  const getPanelStateSpecificStyles = (): string => {
    switch (panelState) {
      case AiVoicePanelState.RECENT_VOICE:
        return 'flexbox spacing-p-l-4 spacing-p-r-4 _position-relative';
      case AiVoicePanelState.DEFAULT:
        return `spacing-p-r-4 spacing-p-l-4 ${props.contentClassname ?? ''}`;
      case AiVoicePanelState.AI_WRITER:
        return 'spacing-p-0 spacing-p-r-0 spacing-p-l-0 radius-8';
      default:
        return '';
    }
  };

  const getContent = (): ReactElement => {
    return (
      <PanelContent
        className={`_full-width ${param.small && !(isPanelDefault() && isOpenedInPopOver) ? styles.scrollableContent : ''} ${isPanelDefault() && isOpenedInPopOver ? styles.visibleContent : ''} ${getPanelStateSpecificStyles()}`}
      >
        {getInnerContent()}
      </PanelContent>
    );
  };

  const updatePanelState = (heightWatcherState: AiVoicePanelHeightChangingEvents): void => {
    dispatch(updatePanelData({panelId: panelId.current, data: {state: heightWatcherState}}));
  };

  const onInitError = (e: ErrorType): void => {
    if (e.code === USER_LOGGED_OUT_ERROR_CODE) {
      setAreRecentVoiceAvailable(false);
    } else {
      console.error(e);
    }
  };

  const init = async (): Promise<void> => {
    try {
      const response = await getUserGeneratedSpeech(0, 1);
      setAreRecentVoiceAvailable(response.length > 0);
      setTimeout(() => {
        inputRef.current?.focus();
      }, 50);
      updateGenerationState(AiVoicePanelGenerationState.IDLE);
    } catch (e: unknown) {
      onInitError(e as ErrorType);
    }
    updatePanelState(AiVoicePanelHeightChangingEvents.DEFAULT);
  };

  useEffect(() => {
    void init();
    return noop;
  }, []);

  return (
    <Panel
      ref={panelRef}
      className="_full-width _full-height"
      panelId={props.panelId}
      key={props.panelId}
      panelFooter={getFooter()}
      panelHeader={getHeader()}
      panelContent={getContent()}
      panelMessage={getPanelMessage}
    />
  );
}
